import React from "react"
import { FormContainer, Worksheet, LabeledInput, CheckboxYesNo } from "uikit"
import { Dropdown } from "semantic-ui-react"
import { Footer } from "./Footer"
import { useStore } from "store"

const HomePurchaseForm = ({
  data: {
    fields: {
      year,
      month,
      amount,
      propertyTaxRateChoice,
      property_tax_rate,
      use_financing,
      credit_rating,
      is_first_home,
      will_sell_existing_house,
      property_id,
      purchase_price
    },
    isModified,
    onSubmit,
    isPending,
    hasError,
    error
  },
  currentYear,
  propertyTaxRateChoices,
  yearOptions,
  creditRatingOptions,
  submitLabel = "Done",
  submitHeader = "",
  onDelete,
  baseUrl,
  isNew,
  monthOptions,
  propertyOptions,
  askIfFirstHome,
  getPropertyPrice
}) => (
  <Worksheet error={error}>
    <Worksheet.Body>
      <Worksheet.Row
        label="In what year do you want to buy a home?"
        paddedField
      >
        <Worksheet.Field error={year.error}>
          <Dropdown {...year} search selection options={yearOptions} fluid />
        </Worksheet.Field>

        {year.value === new Date().getFullYear().toString() ? (
          <Worksheet.Row label="In what month?" paddedField>
            <Worksheet.Field>
              <Dropdown
                selection
                {...month}
                fluid
                placeholder="Month"
                options={monthOptions}
              />
            </Worksheet.Field>
          </Worksheet.Row>
        ) : null}
      </Worksheet.Row>
      <Worksheet.Row
        label="How much do you think that home will cost?"
        paddedField
      >
        <Worksheet.Field error={amount.error}>
          <LabeledInput
            {...amount}
            label=""
            iconText="$"
            iconPosition="left"
            placeholder="0"
          />
        </Worksheet.Field>
      </Worksheet.Row>

      <Worksheet.Row
        label="What property tax rate should we plan for?"
        paddedField
      >
        <Worksheet.Field error={propertyTaxRateChoice.error}>
          <Dropdown
            fluid
            {...propertyTaxRateChoice}
            selection
            placeholder="Tax Rate"
            options={propertyTaxRateChoices}
            onChange={(event, data) => {
              if (data.value !== "Other") {
                property_tax_rate.onChange(event, data)
              }
              propertyTaxRateChoice.onChange(event, data)
            }}
          />
        </Worksheet.Field>

        {propertyTaxRateChoice.value === "Other" ||
        !!property_tax_rate.error ? (
          <Worksheet.Row
            label="Enter the specific effective property tax rate you want to plan for"
            description="The effective property tax rate is the annual property tax amount divided by the current market value of the property (multiplied by 100 and expressed as in percentage). Effective property tax rates can be very different from published property tax rates typically based on adjusted appraised value which is not always the same as the current value estimates."
            paddedField
          >
            <Worksheet.Field error={property_tax_rate.error}>
              <LabeledInput
                {...property_tax_rate}
                label=""
                iconText="%"
                iconPosition="right"
                placeholder="0"
              />
            </Worksheet.Field>
          </Worksheet.Row>
        ) : null}
      </Worksheet.Row>
      <Worksheet.Row
        label="Consider financing the home purchase with a mortgage?"
        paddedField
      >
        <Worksheet.Field>
          <CheckboxYesNo
            fluid
            checked={use_financing.value === "true"}
            {...use_financing}
          />
        </Worksheet.Field>
        {use_financing.value === "true" ? (
          <Worksheet.Row label="What is your credit rating?" paddedField>
            <Worksheet.Field error={credit_rating.error}>
              <Dropdown
                fluid
                {...credit_rating}
                search
                selection
                options={creditRatingOptions}
              />
            </Worksheet.Field>
          </Worksheet.Row>
        ) : null}
      </Worksheet.Row>

      {askIfFirstHome ? (
        <Worksheet.Row
          label="Is this a first time home purchase?"
          description="The IRS allows individuals to access their IRA accounts penalty-free if they have not owned a home in the past two years or previously used their first-time home buyer exemption."
          paddedField
        >
          <Worksheet.Field error={is_first_home.error}>
            <CheckboxYesNo fluid {...is_first_home} />
          </Worksheet.Field>
        </Worksheet.Row>
      ) : null}
      {is_first_home.value === "false" && propertyOptions.length ? (
        <Worksheet.Row
          label="Will you sell your existing house when buying this new house?"
          paddedField
        >
          <Worksheet.Field error={will_sell_existing_house.error}>
            <CheckboxYesNo fluid {...will_sell_existing_house} />
          </Worksheet.Field>
          {will_sell_existing_house.checked ? (
            <Worksheet.RowCollection>
              <Worksheet.Row
                label="What property will you sell when buying this new house?"
                paddedField
              >
                <Worksheet.Field error={property_id.error}>
                  <Dropdown
                    selection
                    {...property_id}
                    fluid
                    placeholder="House"
                    options={propertyOptions}
                    onChange={(event, data) => {
                      purchase_price.onChange(event, {
                        value: getPropertyPrice(data.value)
                      })
                      property_id.onChange(event, data)
                    }}
                  />
                </Worksheet.Field>
              </Worksheet.Row>

              <Worksheet.Row
                label="What was the original purchase price of this house you are selling?"
                paddedField
              >
                <Worksheet.Field error={purchase_price.error}>
                  <LabeledInput
                    {...purchase_price}
                    label=""
                    iconText="$"
                    iconPosition="left"
                    placeholder="0"
                  />
                </Worksheet.Field>
              </Worksheet.Row>
            </Worksheet.RowCollection>
          ) : null}
        </Worksheet.Row>
      ) : null}
    </Worksheet.Body>
    <Footer
      baseUrl={baseUrl}
      isNew={isNew}
      submitLabel={submitLabel}
      onSubmit={onSubmit}
      isPending={isPending}
      onDelete={onDelete}
      isModified={isModified}
      deleteLabel="Delete Goal"
      error={error}
    />
  </Worksheet>
)

const HomePurchaseFormContainer = FormContainer(
  {
    propertyTaxRateChoice: {
      type: "string",
      required: false,
      submit: false
    },
    year: {
      type: "unit",
      useGrouping: false,
      minValue: new Date().getFullYear(),
      maxValue: new Date().getFullYear() + 25
    },
    month: {
      type: "unit",
      defaultValue: 12,
      required: false
    },
    amount: {
      type: "unit",
      minValue: 10000
    },
    property_tax_rate: {
      type: "decimal",
      minValue: "0",
      maxValue: "100"
    },
    credit_rating: {
      type: "string",
      defaultValue: "good"
    },
    use_financing: {
      type: "boolean"
    },
    type: {
      type: "string",
      defaultValue: "homepurchase"
    },
    is_first_home: {
      type: "boolean",
      required: false
    },
    property_id: {
      type: "string",
      required: false
    },
    purchase_price: {
      type: "unit",
      required: false
    },
    will_sell_existing_house: {
      type: "boolean",
      required: false,
      defaultValue: false
    }
  },
  (
    { fields: { will_sell_existing_house, property_id, purchase_price } },
    ...form
  ) => {
    if (will_sell_existing_house.value === "true") {
      if (!property_id.value) {
        return {
          fields: {
            property_id: `This field is required.`
          }
        }
      }
      if (purchase_price.value === "") {
        return {
          fields: {
            purchase_price: `This field is required.`
          }
        }
      }
    }
  }
)(HomePurchaseForm)

const HomePurchase = ({ household, scenario, goal, ...formProps }) => {
  const [people] = useStore("householdPeople", household.id)
  const [
    properties,
    { addGoal, updatePerson, updateGoal, deleteGoal, updateProperty }
  ] = useStore("householdProperties", household.id)
  const [householdIncomesObject] = useStore("householdIncomes", household.id)

  let values = { is_first_home: household.is_renter }

  const isNew = goal === null
  let currentYear = new Date().getFullYear()
  let propertyTaxRateChoices = [
    { value: "0.75", text: "0.75% (Low)" },
    { value: "1.25", text: "1.25% (Moderate)" },
    { value: "2", text: "2% (High)" },
    { value: "3", text: "3% (Very High)" },
    {
      text: "Other",
      value: "Other"
    }
  ]

  const yearOptions = Array(26)
    .fill()
    .map((_, i) => ({
      value: currentYear + i + "",
      text: currentYear + i
    }))

  const creditRatingOptions = ["Fair", "Good", "Excellent"].map(label => ({
    text: label,
    value: label.toLowerCase()
  }))
  const allMonthOptions = [
    { value: "1", text: "January" },
    { value: "2", text: "February" },
    { value: "3", text: "March" },
    { value: "4", text: "April" },
    { value: "5", text: "May" },
    { value: "6", text: "June" },
    { value: "7", text: "July" },
    { value: "8", text: "August" },
    { value: "9", text: "September" },
    { value: "10", text: "October" },
    { value: "11", text: "November" },
    { value: "12", text: "December" }
  ]
  const currentMonth = new Date().getMonth()
  const monthOptions = allMonthOptions.filter(
    month => month.value >= currentMonth + 1
  )
  const client = Object.values(people).find(person => person.role === "client")
  const rentalIncomes =
    Object.values(householdIncomesObject)
      .filter(i => i.type === "rental")
      .map(i => i.property_id) || []
  const propertyOptions = Object.values(properties)
    .filter(property => property.type === "realestate")
    .filter(
      p =>
        p.is_primary_residence ||
        (!p.is_primary_residence && !rentalIncomes.some(r => r === p.id))
    )
    .map(
      ({
        description: text,
        id: value,
        purchase_price: purchased_price,
        is_primary_residence
      }) => ({
        text,
        value,
        purchased_price
      })
    )
  if (!isNew) {
    values = { ...goal }
    const propertyTaxRateChosen = propertyTaxRateChoices.find(
      ({ value }) => value === values.property_tax_rate.toString()
    )
    if (propertyTaxRateChosen) {
      values.propertyTaxRateChoice = propertyTaxRateChosen.value
    } else {
      values.propertyTaxRateChoice = "Other"
    }
    let will_sell_existing_house = false
    let purchase_price = null
    if (values.property_id) {
      will_sell_existing_house = true
      const houseDetails = Object.values(properties).find(
        property => property.id === values.property_id
      )
      purchase_price = houseDetails ? houseDetails.purchase_price : null
    }
    Object.assign(values, { will_sell_existing_house, purchase_price })
    const credit_rating =
      client && client.credit_rating ? client.credit_rating : ""
    Object.assign(values, {
      credit_rating
    })
  } else {
    Object.assign(values, {
      is_first_home: propertyOptions.length === 0,
      property_id:
        propertyOptions.length === 1 ? propertyOptions[0].value : null,
      purchase_price:
        propertyOptions.length === 1 && propertyOptions[0].purchased_price
          ? propertyOptions[0].purchased_price
          : null
    })
  }
  const getPropertyPrice = propertyId => {
    const property = propertyOptions.find(
      property => property.value === propertyId
    )
    return property && property.purchased_price
      ? property.purchased_price
      : null
  }
  const submit = isNew
    ? async ({ financing, credit_rating, ...goalData }) => {
        let hasHouseSale = false
        let purchased_price = 0
        if (goalData.will_sell_existing_house) {
          hasHouseSale = true
          purchased_price = goalData.purchase_price
        } else {
          Object.assign(goalData, { property_id: null })
        }
        delete goalData.will_sell_existing_house
        delete goalData.purchase_price
        await addGoal({
          scenario_id: scenario.id,
          household_id: household.id,
          ...goalData
        })
        await updatePerson({
          id: client.id,
          credit_rating
        })
        if (hasHouseSale) {
          await updateProperty({
            id: goalData.property_id,
            purchase_price: purchased_price,
            type: "realestate"
          })
        }
      }
    : async ({ financing, credit_rating, ...goalData }) => {
        let hasHouseSale = false
        let purchased_price = 0
        if (goalData.will_sell_existing_house) {
          hasHouseSale = true
          purchased_price = goalData.purchase_price
        } else {
          Object.assign(goalData, { property_id: null })
        }
        delete goalData.will_sell_existing_house
        delete goalData.purchase_price

        if (goalData.year !== new Date().getFullYear()) {
          Object.assign(goalData, { month: 12 })
        }
        await updateGoal({ id: goal.id, ...goalData })

        await updatePerson({
          id: client.id,
          credit_rating
        })
        if (hasHouseSale) {
          await updateProperty({
            id: goalData.property_id,
            purchase_price: purchased_price,
            type: "realestate"
          })
        }
      }

  const onDelete = async () => {
    await deleteGoal({ id: goal.id, scenario_id: scenario.id })
    return true
  }
  return (
    <HomePurchaseFormContainer
      {...{
        submit,
        onDelete,
        values,
        creditRatingOptions,
        yearOptions,
        propertyTaxRateChoices,
        currentYear,
        isNew,
        monthOptions,
        propertyOptions,
        askIfFirstHome: values.is_first_home,
        getPropertyPrice,
        ...formProps
      }}
    />
  )
}

export default HomePurchase
